import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, HStack, VStack, Text, Badge, Button, Checkbox } from "@chakra-ui/react";
import Quill from "quill";
// Registers the clinic page's Quill formats (class colors, vitals / ROS grids,
// task links, signed images...) so a note renders here exactly as it does
// on /clinic/<id>.
import "../medstudent/DocumentBody";
import {
  opsOf, buildChanges, defaultChoices, mergedDocument, redline, changeAt, previewText, MARK,
} from "./docMerge";

/**
 * A clinic note rendered as the page the doctor sees (same Quill formats,
 * same clinic.css look), redlined with what the sync will do to it:
 *
 *   blue    -- only RDS's copy has it        (kept)
 *   green   -- only Supabase's copy has it   (kept)
 *   yellow  -- same words, formatting differs
 *   red, struck through -- left out of the result
 *
 * mode "merge": both copies are merged; click a highlighted passage (or its
 * box in the list) to keep / leave it out, and onMerged receives the merged
 * .json every time the choice changes. Any other mode shows, read-only, what
 * replacing one copy with the other does (keep = "rds" | "supabase").
 */

const KIND = {
  rds: { label: "only in RDS", scheme: "blue" },
  supabase: { label: "only in Supabase", scheme: "green" },
  format: { label: "formatting differs", scheme: "yellow" },
};

function fixedChoices(changes, keep) {
  const out = {};
  changes.forEach((c) => {
    if (c.kind === "rds") out[c.id] = keep === "rds";
    if (c.kind === "supabase") out[c.id] = keep === "supabase";
    if (c.kind === "format") out[c.id] = keep === "supabase";
  });
  return out;
}

function lockEmbeds(root) {
  root.querySelectorAll("input[data-vital]").forEach((el) => el.setAttribute("readonly", "readonly"));
  root.querySelectorAll("[data-vital-label],[data-ros-label],[data-ros]").forEach((el) => {
    el.setAttribute("contenteditable", "false");
  });
}

// The change list draws this many rows at first; a note with hundreds of
// differences would otherwise build hundreds of checkboxes up front.
const LIST_PAGE = 100;
// Unchanged paragraphs drawn on each side of a change when folded.
const CONTEXT_PARAGRAPHS = 1;

/**
 * loadChoices() / onChoices: the operator's keep / leave-out picks survive
 * the note being collapsed and opened again (only one is mounted at a time).
 * The parent keys this component on mode + keep, so choices are set once at
 * mount instead of being reset by an effect (which drew the page twice).
 */
function DocumentMergeView({ rdsDoc, supabaseDoc, mode = "merge", keep, onMerged, loadChoices, onChoices }) {
  const merging = mode === "merge";
  const changes = useMemo(() => buildChanges(opsOf(rdsDoc) || [], opsOf(supabaseDoc) || []), [rdsDoc, supabaseDoc]);
  const [choices, setChoices] = useState(() => {
    if (!merging) return fixedChoices(changes, keep);
    const base = defaultChoices(changes);
    // Saved picks only apply to changes this comparison still has.
    const savedChoices = loadChoices ? loadChoices() : null;
    if (savedChoices) Object.keys(base).forEach((id) => { if (id in savedChoices) base[id] = savedChoices[id]; });
    return base;
  });
  const [listLimit, setListLimit] = useState(LIST_PAGE);
  // Only what changes, with a paragraph of context; unchanged text is folded.
  const [whole, setWhole] = useState(false);

  const view = useMemo(
    () => redline(changes, choices, whole ? {} : { context: CONTEXT_PARAGRAPHS }),
    [changes, choices, whole],
  );
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const scrollRef = useRef(null);
  const rangesRef = useRef([]);
  rangesRef.current = view.ranges;
  const toggleRef = useRef(null);
  toggleRef.current = (id) => {
    if (!merging || !id) return;
    setChoices((c) => ({ ...c, [id]: !c[id] }));
  };

  useEffect(() => {
    if (!hostRef.current || quillRef.current) return undefined;
    const quill = new Quill(hostRef.current, { theme: "snow", readOnly: true, modules: { toolbar: false } });
    quill.enable(false);
    if (quill.container) quill.container.style.border = "none";
    quillRef.current = quill;
    const onClick = (e) => {
      const blot = Quill.find(e.target, true);
      if (!blot || blot === quill.scroll) return;
      const hit = changeAt(rangesRef.current, quill.getIndex(blot));
      if (hit) toggleRef.current(hit.id);
    };
    quill.root.addEventListener("click", onClick);
    return () => quill.root.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const top = scrollRef.current?.scrollTop || 0;
    quill.setContents(view.ops, "silent");
    lockEmbeds(quill.root);
    quill.root.style.cursor = merging ? "pointer" : "default";
    if (scrollRef.current) scrollRef.current.scrollTop = top;
  }, [view, merging]);

  useEffect(() => {
    if (!merging) return;
    if (onMerged) onMerged(mergedDocument(rdsDoc, supabaseDoc, changes, choices));
    if (onChoices) onChoices(choices);
  }, [merging, onMerged, onChoices, rdsDoc, supabaseDoc, changes, choices]);

  const listed = changes.filter((c) => c.kind !== "same");
  const setAll = (fn) => setChoices(Object.fromEntries(listed.map((c) => [c.id, fn(c)])));
  const kept = listed.filter((c) => c.kind !== "format" && choices[c.id]).length;

  return (
    <Box data-testid="document-merge">
      <HStack spacing={3} mb={2} flexWrap="wrap" fontSize="xs">
        <Box as="span" px={1} style={{ background: MARK.rds.background }}>only in RDS</Box>
        <Box as="span" px={1} style={{ background: MARK.supabase.background }}>only in Supabase</Box>
        <Box as="span" px={1} style={{ background: MARK.format.background }}>formatting differs</Box>
        <Box as="span" px={1} color="red.600" textDecoration="line-through" style={{ background: MARK.dropped.background }}>
          left out
        </Box>
        {merging && <Text color="gray.500">Click a highlighted passage to keep it or leave it out.</Text>}
        <Checkbox size="sm" isChecked={whole} onChange={(e) => setWhole(e.target.checked)}
                  data-testid="document-merge-whole">
          <Text fontSize="xs">Show whole note</Text>
        </Checkbox>
      </HStack>
      {listed.length === 0 && (
        <Text fontSize="xs" color="gray.600" mb={2}>Both copies of the note read the same.</Text>
      )}
      <HStack align="stretch" spacing={3}>
        <Box ref={scrollRef} id="clinic-root" flex="1" maxH="640px" overflowY="auto" bg="#f1f5f9" p={4} borderRadius="md">
          <Box bg="white" maxW="48rem" mx="auto" px={{ base: 4, md: 12 }} py={10} minH="420px"
               boxShadow="0 1px 3px rgba(0,0,0,0.12), 0 8px 24px rgba(15,23,42,0.08)" border="1px solid #e2e8f0">
            <div ref={hostRef} className="text-slate-700" data-testid="document-merge-page" />
          </Box>
        </Box>
        {listed.length > 0 && (
          <VStack align="stretch" w="260px" flexShrink={0} spacing={1} maxH="640px" overflowY="auto" fontSize="xs"
                  data-testid="document-merge-changes">
            <Text fontWeight="bold">
              {listed.length} change{listed.length === 1 ? "" : "s"}{merging ? ` · ${kept} kept` : ""}
            </Text>
            {merging && (
              <HStack spacing={1} flexWrap="wrap">
                <Button size="xs" variant="outline" onClick={() => setAll(() => true)}>Keep all</Button>
                <Button size="xs" variant="outline" onClick={() => setAll((c) => c.kind !== "supabase" && c.kind !== "format")}>
                  RDS's only
                </Button>
                <Button size="xs" variant="outline" onClick={() => setAll((c) => c.kind !== "rds")}>Supabase's only</Button>
              </HStack>
            )}
            {listed.slice(0, listLimit).map((c, i) => (
              <HStack key={c.id} align="flex-start" spacing={2} p={1} borderRadius="sm" bg={choices[c.id] ? undefined : "red.50"}
                      data-testid="document-merge-change">
                <Checkbox size="sm" isChecked={!!choices[c.id]} isDisabled={!merging}
                          onChange={() => toggleRef.current(c.id)}
                          aria-label={`${c.kind === "format" ? "use Supabase's formatting for" : "keep"} change ${i + 1}`} />
                <Box>
                  <Badge colorScheme={KIND[c.kind].scheme} fontSize="9px">{KIND[c.kind].label}</Badge>
                  <Text color="gray.700" textDecoration={!choices[c.id] && c.kind !== "format" ? "line-through" : undefined}>
                    {previewText(c.ops)}
                  </Text>
                  {c.kind === "format" && (
                    <Text color="gray.500">{choices[c.id] ? "Supabase's formatting" : "RDS's formatting"}</Text>
                  )}
                </Box>
              </HStack>
            ))}
            {listed.length > listLimit && (
              <Button size="xs" variant="ghost" onClick={() => setListLimit((l) => l + LIST_PAGE)}>
                Show {Math.min(LIST_PAGE, listed.length - listLimit)} more of {listed.length - listLimit}
              </Button>
            )}
          </VStack>
        )}
      </HStack>
    </Box>
  );
}

export default React.memo(DocumentMergeView);
